/**
 * Store Zustand para filtros dos relatórios
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Tipos de status de processo
export type ProcessStatusFilter = 'concluido' | 'andamento' | 'atrasado';

// Modo de visualização de fornecedores
export type SupplierViewMode = 'todos' | 'equipes' | 'customizado';

// Escopo de documentos da seção Imóveis
export type ImoveisScope = 'todos' | 'avcb' | 'alvara';

// Grupo de fornecedores customizado
export interface SupplierGroup {
  id: string;
  name: string;
  suppliers: string[];  // Lista de nomes de fornecedores no grupo
  color?: string;       // Cor opcional para o grupo
}

interface RelatoriosFiltersState {
  // Filtros de Desempenho (todos multi-seleção)
  selectedRegionals: Set<string>;  // Regionais selecionadas
  selectedYears: Set<string>;      // Anos selecionados
  selectedMonths: Set<string>;     // Meses selecionados
  selectedDocTypes: Set<string>;   // Tipos de documento
  selectedSuppliers: Set<string>;  // Fornecedores selecionados
  selectedStatus: Set<ProcessStatusFilter>;  // Status dos processos
  equipesOnly: boolean;
  
  // Grupos de fornecedores customizados
  supplierGroups: SupplierGroup[];
  supplierViewMode: SupplierViewMode;
  
  // Filtros legados (para compatibilidade com outras seções)
  regional: string | null;
  uf: string | null;
  
  // View mode para Imóveis
  viewMode: 'absoluto' | 'percentual';
  imoveisScope: ImoveisScope;
  
  // Seção ativa (para navegação)
  activeSection: string;
  
  // Actions
  setSelectedRegionals: (regionals: Set<string>) => void;
  setSelectedYears: (years: Set<string>) => void;
  setSelectedMonths: (months: Set<string>) => void;
  setSelectedDocTypes: (docTypes: Set<string>) => void;
  setSelectedSuppliers: (suppliers: Set<string>) => void;
  setSelectedStatus: (status: Set<ProcessStatusFilter>) => void;
  setEquipesOnly: (equipesOnly: boolean) => void;
  setRegional: (regional: string | null) => void;
  setUF: (uf: string | null) => void;
  setViewMode: (mode: 'absoluto' | 'percentual') => void;
  setImoveisScope: (scope: ImoveisScope) => void;
  setActiveSection: (section: string) => void;
  
  // Actions para grupos de fornecedores
  setSupplierViewMode: (mode: SupplierViewMode) => void;
  addSupplierGroup: (group: Omit<SupplierGroup, 'id'>) => void;
  updateSupplierGroup: (id: string, group: Partial<Omit<SupplierGroup, 'id'>>) => void;
  deleteSupplierGroup: (id: string) => void;
  
  clearFilters: () => void;
}

// Helper para gerar ID único
const generateId = () => Math.random().toString(36).substring(2, 9);

// Cores padrão para grupos
export const GROUP_COLORS = [
  '#8b5cf6', // Roxo
  '#f59e0b', // Laranja
  '#ec4899', // Rosa
  '#14b8a6', // Teal
  '#f97316', // Laranja escuro
  '#6366f1', // Indigo
];

export const useRelatoriosFilters = create<RelatoriosFiltersState>()(
  persist(
    (set, get) => ({
      // Estado inicial - Desempenho
      selectedRegionals: new Set(),
      selectedYears: new Set(),
      selectedMonths: new Set(),
      selectedDocTypes: new Set(),
      selectedSuppliers: new Set(),
      selectedStatus: new Set(['concluido', 'atrasado']), // Por padrão: concluídos e atrasados
      equipesOnly: true, // Por padrão, mostrar apenas equipes
      
      // Grupos de fornecedores
      supplierGroups: [],
      supplierViewMode: 'equipes',
      
      // Estado inicial - Legado
      regional: null,
      uf: null,
      viewMode: 'absoluto',
      imoveisScope: 'todos',
      activeSection: 'imoveis',
      
      // Actions - Desempenho
      setSelectedRegionals: (selectedRegionals) => set({ selectedRegionals }),
      setSelectedYears: (selectedYears) => set({ selectedYears }),
      setSelectedMonths: (selectedMonths) => set({ selectedMonths }),
      setSelectedDocTypes: (selectedDocTypes) => set({ selectedDocTypes }),
      setSelectedSuppliers: (selectedSuppliers) => set({ selectedSuppliers }),
      setSelectedStatus: (selectedStatus) => set({ selectedStatus }),
      setEquipesOnly: (equipesOnly) => set({ equipesOnly }),
      
      // Actions - Grupos de fornecedores
      setSupplierViewMode: (supplierViewMode) => set({ supplierViewMode }),
      
      addSupplierGroup: (group) => set((state) => ({
        supplierGroups: [
          ...state.supplierGroups,
          { 
            ...group, 
            id: generateId(),
            color: group.color || GROUP_COLORS[state.supplierGroups.length % GROUP_COLORS.length],
          },
        ],
      })),
      
      updateSupplierGroup: (id, updates) => set((state) => ({
        supplierGroups: state.supplierGroups.map((g) =>
          g.id === id ? { ...g, ...updates } : g
        ),
      })),
      
      deleteSupplierGroup: (id) => set((state) => ({
        supplierGroups: state.supplierGroups.filter((g) => g.id !== id),
      })),
      
      // Actions - Legado
      setRegional: (regional) => set({ regional, uf: null }),
      setUF: (uf) => set({ uf }),
      setViewMode: (viewMode) => set({ viewMode }),
      setImoveisScope: (imoveisScope) => set({ imoveisScope }),
      setActiveSection: (activeSection) => set({ activeSection }),
      
      clearFilters: () => set({
        selectedRegionals: new Set(),
        selectedYears: new Set(),
        selectedMonths: new Set(),
        selectedDocTypes: new Set(),
        selectedSuppliers: new Set(),
        selectedStatus: new Set(['concluido', 'atrasado']),
        equipesOnly: false,
        regional: null,
        uf: null,
        viewMode: 'absoluto',
        imoveisScope: 'todos',
      }),
    }),
    {
      name: 'relatorios-filters',
      partialize: (state) => ({
        // Persiste apenas os grupos de fornecedores
        supplierGroups: state.supplierGroups,
      }),
      // Converte Sets para arrays ao serializar e vice-versa
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          const parsed = JSON.parse(str);
          return parsed;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
